// src/components/ProjectDetails.jsx
import React, { useState } from "react";
import ProjectNetworkManager from "./ProjectNetworkManager";
import PartnerAssignField from "./PartnerAssignField";
import { RELATION_TYPES } from "../services/azureRelationsService";

const ProjectDetails = ({
  project,
  employees,
  onEdit,
  onDelete,
  onBack,
  onSave,
}) => {
  const [showNetworkManager, setShowNetworkManager] = useState(false);

  if (!project) {
    return <div className="loading">Projekt wird geladen...</div>;
  }

  const getEmployeeName = (employeeUuid) => {
    const employee = employees.find((emp) => emp.uuid === employeeUuid);
    return employee
      ? `${employee.firstName} ${employee.lastName}`
      : "Unbekannt";
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("de-CH", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("de-CH", {
      style: "currency",
      currency: "CHF",
    }).format(amount);
  };

  const openLocationInMaps = () => {
    if (
      project.location &&
      project.location.latitude &&
      project.location.longitude
    ) {
      const url = `https://www.google.com/maps?q=${project.location.lat},${project.location.longitude}`;
      window.open(url, "_blank");
    }
  };

  const handleNetworkSave = () => {
    console.log("Relations updated for project:", project.id);
  };

  if (showNetworkManager) {
    return (
      <ProjectNetworkManager
        project={project}
        onClose={() => setShowNetworkManager(false)}
        onSave={handleNetworkSave}
      />
    );
  }

  return (
    <>
      <div className="project-details">
        {/* Header */}
        <div className="project-details-header">
          <div className="header-left">
            <button className="btn btn-secondary" onClick={onBack}>
              Zurück
            </button>
          </div>
          <div className="header-right">
            <button
              className="btn btn-primary"
              onClick={() => setShowNetworkManager(true)}
            >
              Netzwerk
            </button>
            <button
              className="btn btn-primary"
              onClick={() => {
                const slug = `${project.code}-${project.title
                  .toLowerCase()
                  .replace(/\s+/g, "-")}`;
                window.location.href = `/projects/${slug}`;
              }}
            >
              Projektseite
            </button>

            <button className="btn btn-primary" onClick={onEdit}>
              Bearbeiten
            </button>
            <button className="btn btn-danger" onClick={onDelete}>
              Löschen
            </button>
          </div>
        </div>

        {/* Project Content */}
        <div className="project-content">
          {/* Title Section */}
          <div className="project-title-section">
            <div className="title-content">
              <h1>{project.title}</h1>
              <div className="project-meta">
                <span className="project-number">#{project.code}</span>
                {project.pic && (
                  <span className="project-pic">
                    PIC: {getEmployeeName(project.pic)}
                  </span>
                )}
              </div>
            </div>

            {/* Project Image */}
            {project.image && (
              <div className="project-image">
                <img src={project.image} alt={project.title} />
              </div>
            )}
          </div>

          {/* Project Info Grid */}
          <div className="project-info-grid">
            {/* Timeline */}
            {(project.projektstart || project.projektende) && (
              <div className="info-card">
                <h3>Zeitraum</h3>
                <div className="timeline">
                  {project.projektstart && (
                    <div className="timeline-item">
                      <label>Projektstart:</label>
                      <span>{formatDate(project.projektstart)}</span>
                    </div>
                  )}
                  {project.projektende && (
                    <div className="timeline-item">
                      <label>Projektende:</label>
                      <span>{formatDate(project.projektende)}</span>
                    </div>
                  )}
                  {project.projektstart && project.projektende && (
                    <div className="timeline-duration">
                      <label>Dauer:</label>
                      <span>
                        {Math.ceil(
                          (new Date(project.projektende) -
                            new Date(project.projektstart)) /
                            (1000 * 60 * 60 * 24)
                        )}{" "}
                        Tage
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Financial */}
            {(project.baukosten > 0 || project.honorar > 0) && (
              <div className="info-card">
                <h3>Finanzen</h3>
                <div className="financial-info">
                  {project.baukosten > 0 && (
                    <div className="financial-item">
                      <label>Baukosten:</label>
                      <span className="amount">
                        {formatCurrency(project.baukosten)}
                      </span>
                    </div>
                  )}
                  {project.honorar > 0 && (
                    <div className="financial-item">
                      <label>Honorar:</label>
                      <span className="amount">
                        {formatCurrency(project.honorar)}
                      </span>
                    </div>
                  )}
                  {project.baukosten > 0 && project.honorar > 0 && (
                    <div className="financial-item ratio">
                      <label>Honorar-Anteil:</label>
                      <span>
                        {((project.honorar / project.baukosten) * 100).toFixed(
                          1
                        )}
                        %
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Location */}
            {(project.adresse ||
              (project.location &&
                project.location.latitude &&
                project.location.longitude)) && (
              <div className="info-card">
                <h3>Standort</h3>
                <div className="location-info">
                  {project.adresse && (
                    <div className="address">
                      <label>Adresse:</label>
                      <span>{project.adresse}</span>
                    </div>
                  )}

                  {project.location &&
                    project.location.latitude &&
                    project.location.longitude && (
                      <div className="coordinates">
                        <label>Koordinaten:</label>
                        <span>
                          {project.location.lat.toFixed(6)},{" "}
                          {project.location.longitude.toFixed(6)}
                        </span>
                        <button
                          className="btn btn-link btn-sm"
                          onClick={openLocationInMaps}
                        >
                          In Karten öffnen
                        </button>
                      </div>
                    )}
                </div>
              </div>
            )}

            {/* Tags */}
            {project.tags && project.tags.length > 0 && (
              <div className="info-card">
                <h3>Tags</h3>
                <div className="tags-display">
                  {project.tags?.map((tag, index) => {
                    const tagName = typeof tag === "object" ? tag.name : tag;
                    const tagColor =
                      typeof tag === "object" ? tag.color : "#6c757d";

                    return (
                      <span
                        key={typeof tag === "object" ? tag.id : `tag-${index}`}
                        className="tag-badge"
                        style={{ backgroundColor: tagColor }}
                      >
                        {tagName || "Unbekannt"}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Descriptions */}
          <div className="descriptions-section">
            {/* Project Description */}
            {project.beschreibung && (
              <div className="description-card">
                <h3>Projektbeschreibung</h3>
                <div className="description-content">
                  {project.beschreibung.split("\n").map((line, index) => (
                    <p key={index}>{line}</p>
                  ))}
                </div>
              </div>
            )}

            {/* Auftraggeberin: echte CRM-Zuordnung (siehe PartnerAssignField in
                ProjectForm); Freitext-Altwert nur noch als Hinweis, falls die
                Firma noch nicht neu zugeordnet wurde. */}
            {project.code && (
              <div className="description-card">
                <h3>Auftraggeberin</h3>
                <div className="description-content">
                  <PartnerAssignField
                    projectCode={project.code}
                    relationType={RELATION_TYPES.AUFTRAGGEBERIN}
                    emptyHint="Keine Firma zugeordnet."
                    disabled
                  />
                  {project.auftraggeberin && (
                    <p className="field-hint">
                      Bisher als Freitext erfasst: {project.auftraggeberin}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Project Meta Information */}
          <div className="project-meta-section">
            <div className="meta-info">
              {project.createdAt && (
                <div className="meta-item">
                  <label>Erstellt:</label>
                  <span>{formatDate(project.createdAt)}</span>
                </div>
              )}
              {project.updatedAt && project.updatedAt !== project.createdAt && (
                <div className="meta-item">
                  <label>Letzte Änderung:</label>
                  <span>{formatDate(project.updatedAt)}</span>
                </div>
              )}
            </div>
          </div>
        </div>

      </div>
    </>
  );
};

export default ProjectDetails;
